import { Download, Search } from 'lucide-react';
import { type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Card,
  DataTable,
  EmptyState,
  ErrorState,
  Field,
  Input,
  useToast,
  type Column,
} from '@/components/ui';
import { formatRM } from '@/features/billing/money';
import {
  maskedIdentityLabel,
  useExportPanelClaimsHistory,
  useFinancePatientPanelSearch,
  type FinancePatientPanelResult,
} from '@/features/panel-history/api';
import { PanelAndClaims } from '@/features/panel-history/PanelAndClaims';
import { usePatientPanelUsageLedger } from '@/features/panel-limits/api';
import { PageContainer } from '@/layouts/PageContainer';
import { PatientPanelUsageLedger } from '@/pages/panel-limits/PatientPanelUsageLedger';
import { isApiError } from '@/lib/api';
import { useOptionalUrlState, useUrlState } from '@/lib/url-state';
import styles from './FinanceByPatientPage.module.css';

const searchErrorKind = (error: unknown) => {
  if (!isApiError(error)) return 'failed' as const;
  switch (error.code) {
    case 'VALIDATION_FAILED':
      return 'invalid' as const;
    case 'FORBIDDEN':
      return 'forbidden' as const;
    default:
      return 'failed' as const;
  }
};

const exportErrorKind = (error: unknown) => {
  if (!isApiError(error)) return 'failed' as const;
  switch (error.code) {
    case 'FORBIDDEN':
      return 'forbidden' as const;
    case 'PANEL_HISTORY_NOT_FOUND':
      return 'empty' as const;
    default:
      return 'failed' as const;
  }
};

export function FinanceByPatientPage() {
  const { t } = useTranslation();
  const [query, setQuery] = useUrlState('q', '');
  const [draft, setDraft] = useOptionalUrlState('draft', { replace: true });
  const input = draft ?? query;
  const [selectedId, setSelectedId] = useOptionalUrlState('patient');
  const search = useFinancePatientPanelSearch(query);
  const selected =
    search.data?.find((patient) => patient.patientId === selectedId) ?? null;
  const exportHistory = useExportPanelClaimsHistory();
  const recentUsage = usePatientPanelUsageLedger();
  const toast = useToast();
  const searchErrorTitle = t(
    `panelHistory.searchError.${searchErrorKind(search.error)}`,
  );
  const exportErrorTitle = t(
    `panelHistory.exportError.${exportErrorKind(exportHistory.error)}`,
  );

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const next = input.trim();
    if (next.length < 2) return;
    setQuery(next);
  };

  const columns: Column<FinancePatientPanelResult>[] = [
    {
      key: 'patient',
      header: t('panelHistory.patient'),
      render: (patient) => (
        <div>
          <strong>{patient.patientName}</strong>
          <span className={styles.muted}>
            {maskedIdentityLabel(patient.identityLabel)}
          </span>
        </div>
      ),
    },
    {
      key: 'panels',
      header: t('panelHistory.panels'),
      render: (patient) => patient.panels.join(', '),
    },
    {
      key: 'claims',
      header: t('panelHistory.claims'),
      render: (patient) => patient.claimCount,
    },
    {
      key: 'claimed',
      header: t('panelHistory.totalClaimed'),
      render: (patient) => formatRM(patient.totalClaimedSen),
    },
    {
      key: 'approved',
      header: t('panelHistory.totalApproved'),
      render: (patient) => formatRM(patient.totalApprovedSen),
    },
    {
      key: 'remaining',
      header: t('panelHistory.remaining'),
      render: (patient) => (
        <div className={styles.statusCell}>
          {patient.remainingLimitSen === null
            ? t('panelHistory.noCap')
            : formatRM(patient.remainingLimitSen)}
          {patient.limitStatus !== 'within_limit' && (
            <Badge
              tone={patient.limitStatus === 'exceeded' ? 'danger' : 'warning'}
            >
              {t(`panelHistory.balanceStatus.${patient.limitStatus}`)}
            </Badge>
          )}
        </div>
      ),
    },
    {
      key: 'action',
      header: t('panelHistory.actions'),
      align: 'right',
      render: (patient) => (
        <Button size="sm" onClick={() => setSelectedId(patient.patientId)}>
          {t('panelHistory.viewLedger')}
        </Button>
      ),
    },
  ];

  return (
    <PageContainer>
      <Card title={t('panelHistory.byPatient')}>
        <form className={styles.searchForm} onSubmit={submit}>
          <Field
            label={t('panelHistory.searchLabel')}
            hint={t('panelHistory.searchHint')}
          >
            <Input
              type="search"
              value={input}
              placeholder={t('panelHistory.searchPlaceholder')}
              onChange={(event) => setDraft(event.target.value)}
            />
          </Field>
          <Button
            type="submit"
            variant="primary"
            icon={<Search size={16} />}
            disabled={input.trim().length < 2}
          >
            {t('panelHistory.search')}
          </Button>
        </form>
      </Card>

      {query && (
        <Card
          title={t('panelHistory.searchResults')}
          actions={
            <Button
              size="sm"
              icon={<Download size={15} />}
              loading={exportHistory.isPending}
              onClick={() =>
                exportHistory.mutate(
                  { query },
                  {
                    onSuccess: (result) =>
                      toast.show({
                        tone: 'success',
                        title: t('panelHistory.exported'),
                        body: t('panelHistory.exportedBody', {
                          fileName: result.fileName,
                          count: result.rowCount,
                        }),
                      }),
                  },
                )
              }
            >
              {t('panelHistory.exportClaims')}
            </Button>
          }
          flush
        >
          <DataTable
            caption={t('panelHistory.searchResults')}
            rows={search.data}
            rowKey={(patient) => patient.patientId}
            columns={columns}
            loading={search.isLoading || search.isFetching}
            error={search.error}
            errorTitle={searchErrorTitle}
            onRetry={() => void search.refetch()}
            empty={
              <EmptyState
                title={t('panelHistory.noPatients')}
                body={t('panelHistory.noPatientsBody')}
              />
            }
          />
          {exportHistory.error && (
            <ErrorState
              error={exportHistory.error}
              title={exportErrorTitle}
              onRetry={() =>
                exportHistory.mutate({
                  query,
                })
              }
            />
          )}
        </Card>
      )}

      {selected && (
        <Card
          title={t('panelHistory.patientLedgerTitle', {
            name: selected.patientName,
          })}
        >
          <PanelAndClaims patientId={selected.patientId} />
        </Card>
      )}

      {!query && (
        <Card title={t('panelLimits.ledgerTitle')} flush>
          <PatientPanelUsageLedger
            entries={recentUsage.data}
            loading={recentUsage.isLoading}
            error={recentUsage.error}
            onRetry={() => void recentUsage.refetch()}
          />
        </Card>
      )}
    </PageContainer>
  );
}
